import { findFirstCustomLayer } from './layers';
import { removeElements } from '../dom/remove';
import LocationKey from './models/location-key';
import { wrapUpdateCall } from './api';
import { newFeatureFilter } from './request-filters';
import { Coast, District, Entity, Island, Municipality, Neighbourhood, Province, Region } from '../locations';
import { Yellow } from '../styles/css';
// import MapSearch from './search-map';

/**
 * @param {MapSearch} ctx - a map search context
 * @param {Object} feature
 * @param {Object} options
 */
function addAreaToMap( ctx, feature, options = {} ) {
    options = Object.assign( {}, {
        addClose: true,
        closeEventDetail: {},
    }, options );

    const map = ctx.map;

    if ( map.getLayer( 'area:fill' ) ) {
        map.removeLayer( 'area:fill' );
    }

    if ( map.getSource( 'area' ) ) {
        map.removeSource( 'area' );
    }

    const srcFeature = {
        geometry: {
            type: feature.geometry.type,
            coordinates: [ feature.geometry.coordinates ],
        },
        type: feature.type,
    };

    map.addSource( 'area', {
        type: 'geojson',
        data: srcFeature,
        tolerance: 0,
    } );

    if ( map.getLayer( 'area-close:fill' ) ) {
        map.removeLayer( 'area-close:fill' );
    }

    if ( map.getSource( 'area-close' ) ) {
        map.removeSource( 'area-close' );
    }

    map.addLayer( {
        id: 'area:fill',
        source: 'area',
        type: 'fill',
        paint: {
            'fill-color': Yellow,
            'fill-outline-color': Yellow,
            'fill-opacity': 0.4,
        },
    }, findFirstCustomLayer( map ) );

    if ( options.addClose ) {
        const eventDetail = Object.assign( {}, {
            triggerStateChange: true,
        }, options.closeEventDetail );

        const source = {
            type: 'geojson',
            data: {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: mostNortherlyPoint( feature.geometry.coordinates ),
                },
                properties: {
                    type: 'eventDispatcher',
                    event: 'twc:mapsearch:remove-search-area',
                    eventDetail: eventDetail,
                    label: ctx.getTemplateString( 'btnClearArea' ),
                },
            },
        };

        const layer = {
            id: 'area-close:fill',
            source: 'area-close',
            type: 'symbol',
            layout: {
                'icon-image': 'close-icon',
                'icon-allow-overlap': true,
            },
        };

        map.addSource( 'area-close', source );

        map.addLayer( layer );

        ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:show-remove-search-area', {
            bubbles: true,
            detail: eventDetail,
        } ) );
    }
}

/**
 * @param {mapboxgl.Map} ctx - the map context to use
 */
function removeAreaFromMap( ctx ) {
    if ( ctx.getLayer( 'area:fill' ) ) {
        ctx.removeLayer( 'area:fill' );
    }

    if ( ctx.getSource( 'area' ) ) {
        ctx.removeSource( 'area' );
    }

    if ( ctx.getLayer( 'area-close:fill' ) ) {
        ctx.removeLayer( 'area-close:fill' );
    }

    if ( ctx.getSource( 'area-close' ) ) {
        ctx.removeSource( 'area-close' );
    }

    document.dispatchEvent( new CustomEvent( 'twc:mapsearch:draw-your-search:remove-features', { bubbles: true } ) );
}

/**
 * @param {mapboxgl.Map} ctx - the map context to use
 */
function removeAreaState( ctx ) {
    removeAreaFromMap( ctx );
    removeElements( 'input[name=area]' );
}

async function getFeatureRequestsForArea( ctx, area ) {
    const locationsInAreaURL = new URL( '/locationsInArea', ctx.apiURL );
    locationsInAreaURL.searchParams.set( 'area', area );

    const response = await window.http( locationsInAreaURL );

    if ( !response.ok ) {
        throw new Error( 'failed getting locations for area' );
    }

    let locationKeys = await response.json();

    if ( locationKeys.length === 0 ) {
        return [];
    }

    let counts = {
        [ Region ]: {
            count: 0,
            keys: [],
        },
        [ Province ]: {
            count: 0,
            keys: [],
        },
        [ Island ]: {
            count: 0,
            keys: [],
        },
        [ Coast ]: {
            count: 0,
            keys: [],
        },
        [ Municipality ]: {
            count: 0,
            keys: [],
        },
        [ District ]: {
            count: 0,
            keys: [],
        },
        [ Neighbourhood ]: {
            count: 0,
            keys: [],
        },
        [ Entity ]: {
            count: 0,
            keys: [],
        },
    };

    locationKeys.forEach( ( s ) => {
        const key = new LocationKey( s );
        counts[ key.type ].count++;
        counts[ key.type ].keys = [
            ...counts[ key.type ].keys,
            key,
        ];
    } );

    let requests = [];

    if ( counts[ Region ].count > 1 ) {
        return requests;
    }

    const meta = {
        focusMapToSearch: false,
        appendFeaturesIfLayerExists: true,
    };

    requests.push( wrapUpdateCall( ctx, () => {
        const filter = newFeatureFilter();

        const bounds = ctx.map.getBounds();
        filter.set( 'boundSW', bounds.getSouthWest().lat + ',' + bounds.getSouthWest().lng );
        filter.set( 'boundNE', bounds.getNorthEast().lat + ',' + bounds.getNorthEast().lng );

        if ( counts[ Province ].count === 1 ) {
            filter.set( 'excludeLocationIDs', [
                counts[ Province ].keys[ 0 ].id,
            ] );
        }

        const options = {
            fetchFeatures: true,
            featureSearchURL: new URL( `/${Province}`, ctx.apiURL ),
            featureSearchParams: filter,
        };

        return {
            options: options,
            meta: meta,
        };
    } ) );

    if ( counts[ Province ].count === 1 && counts[ Municipality ].count > 1 ) {
        requests.push( wrapUpdateCall( ctx, () => {
            const filter = newFeatureFilter();

            filter.set( 'parentType', Province );
            filter.set( 'parentID', counts[ Province ].keys[ 0 ].id );

            const options = {
                fetchFeatures: true,
                featureSearchURL: new URL( `/${Municipality}`, ctx.apiURL ),
                featureSearchParams: filter,
            };

            return {
                options: options,
                meta: meta,
            };
        } ) );
    }

    if ( counts[ Province ].count === 1 && counts[ Municipality ].count > 0 ) {
        requests.push( wrapUpdateCall( ctx, () => {
            const filter = newFeatureFilter();

            filter.set( 'parentType', Province );
            filter.set( 'parentID', counts[ Province ].keys[ 0 ].id );

            const options = {
                fetchFeatures: true,
                featureSearchURL: new URL( `/${Municipality}`, ctx.apiURL ),
                featureSearchParams: filter,
            };

            return {
                options: options,
                meta: meta,
            };
        } ) );
    }

    const crossSingleMunicipality = counts[ Municipality ].count === 1;
    const crossesDistricts = counts[ District ].count > 0;
    const crossesNeighbourhoods = counts[ Neighbourhood ].count > 0;

    const loadDistricts = (
        crossesDistricts &&
        crossSingleMunicipality
    );

    if ( loadDistricts ) {
        requests.push( wrapUpdateCall( ctx, () => {
            const filter = newFeatureFilter();

            filter.set( 'parentType', Municipality );
            filter.set( 'parentID', counts[ Municipality ].keys[ 0 ].id );

            const options = {
                fetchFeatures: true,
                featureSearchURL: new URL( `/${District}`, ctx.apiURL ),
                featureSearchParams: filter,
            };

            return {
                options: options,
                meta: meta,
            };
        } ) );
    }

    const loadNeighbourhoods = (
        crossesNeighbourhoods &&
        crossSingleMunicipality &&
        counts[ District ].count <= 1
    );

    if ( loadNeighbourhoods ) {
        requests.push( wrapUpdateCall( ctx, () => {
            const filter = newFeatureFilter();

            if ( counts[ Municipality ].keys.length > 0 ) {
                filter.set( 'parentType', Municipality );
                filter.set( 'parentID', counts[ Municipality ].keys[ 0 ].id );
            }

            const options = {
                fetchFeatures: true,
                featureSearchURL: new URL( `/${Neighbourhood}`, ctx.apiURL ),
                featureSearchParams: filter,
            };

            return {
                options: options,
                meta: meta,
            };
        } ) );
    }

    const shouldLoadEntities = () => {
        if ( !crossSingleMunicipality ) {
            return false;
        }
        if ( counts[ Entity ].count === 0 ) {
            return false;
        }
        if ( crossesNeighbourhoods && !crossesDistricts && counts[ Neighbourhood ].count > 1 ) {
            return false;
        }
        if ( crossesDistricts && !crossesNeighbourhoods && counts[ District ].count > 1 ) {
            return false;
        }
        return true;
    };

    if ( shouldLoadEntities() ) {
        requests.push( wrapUpdateCall( ctx, () => {
            const filter = newFeatureFilter();

            if ( counts[ District ].keys.length === 1 ) {
                filter.set( 'parentType', District );
                filter.set( 'parentID', counts[ District ].keys[ 0 ].id );
            } else if ( counts[ Neighbourhood ].keys.length === 1 ) {
                filter.set( 'parentType', Neighbourhood );
                filter.set( 'parentID', counts[ Neighbourhood ].keys[ 0 ].id );
            } else {
                filter.set( 'parentType', Municipality );
                filter.set( 'parentID', counts[ Municipality ].keys[ 0 ].id );
            }

            const options = {
                fetchFeatures: true,
                featureSearchURL: new URL( `/${Entity}`, ctx.apiURL ),
                featureSearchParams: filter,
            };

            return {
                options: options,
                meta: meta,
            };
        } ) );
    }

    return requests;
}

function mostNortherlyPoint( coordinates ) {
    let northerly = coordinates[ 0 ];

    coordinates.forEach( ( coord ) => {
        const northerlyLat = northerly[ 1 ];
        const lat = coord[ 1 ];

        if ( lat > northerlyLat ) {
            northerly = coord;
        }
    } );

    return northerly;
}

function mostNorthEasterlyPoint( coordinates ) {
    let point = coordinates[ 0 ];

    coordinates.forEach( ( coord ) => {
        const currentLon = point[ 0 ];
        const currentLat = point[ 1 ];

        const lon = coord[ 0 ];
        const lat = coord[ 1 ];

        if ( lon > currentLon && lat > currentLat ) {
            point = coord;
        }
    } );

    return point;
}

function parseArea( area ) {
    const parts = area.split( ',' );
    let parsed = [];

    for ( let i = 1; i < parts.length; i++ ) {
        parsed = [ ...parts,
            parts[ i - 1 ],
            parts[ i ],
        ];
    }

    return parsed;
}

export {
    addAreaToMap,
    removeAreaFromMap,
    removeAreaState,
    getFeatureRequestsForArea,
    mostNorthEasterlyPoint,
    mostNortherlyPoint,
    parseArea,
};
